import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FaBars, FaTimes } from "react-icons/fa";
import AdminDropdownMenu from "./AdminDropdownMenu";

const AdminNavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isTransparent, setIsTransparent] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const hamburgerRef = useRef<HTMLButtonElement | null>(null);

  // Scroll-based background (transparent → dark)
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsTransparent(false);
      } else {
        setIsTransparent(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    // run once in case user isn't at top
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const toggleDropdown = () => {
    setIsDropdownOpen((open) => !open);
  };

  // Close dropdown if clicked outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const isAdminRoot = location.pathname === "/admin";

  // Use header-like dark tone when transparent, white when solid
  const linkClasses =
    "font-bold transition duration-200 " +
    (isTransparent
      ? "text-gray-800 hover:text-gray-600"
      : "text-white hover:text-gray-300");

  const iconClasses =
    "text-3xl focus:outline-none transition duration-300 ease-in-out transform hover:scale-110 " +
    (isTransparent ? "text-gray-800" : "text-white");

  return (
    <>
      <div
        className={`fixed top-[60px] left-0 right-0 z-50 transition duration-300 ease-in-out ${
          isTransparent ? "bg-transparent" : "bg-gray-900 shadow-md"
        } p-4 flex justify-between items-center`}
        style={{ height: "50px" }}
      >
        {/* Back to Dashboard Link (hide text on /admin to avoid redundancy) */}
        {isAdminRoot ? (
          <span className="font-bold text-transparent select-none">.</span>
        ) : (
          <Link to="/admin" className={linkClasses}>
            Ridgetop Admin
          </Link>
        )}

        {/* Hamburger Icon (always visible for logout) */}
        <button
          ref={hamburgerRef}
          onClick={toggleDropdown}
          className={iconClasses}
        >
          {isDropdownOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      {/* Dropdown Menu (with Sign Out) */}
      {isDropdownOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-2 right-4 bg-gray-900 text-white rounded-md shadow-lg p-4 w-[200px]"
          style={{
            position: "absolute",
            top: hamburgerRef.current
              ? hamburgerRef.current.getBoundingClientRect().bottom +
                window.scrollY
              : 0,
            left: hamburgerRef.current
              ? hamburgerRef.current.getBoundingClientRect().left - 160
              : 0,
          }}
        >
          <AdminDropdownMenu onSignOut={handleSignOut} />
        </div>
      )}
    </>
  );
};

export default AdminNavBar;
